import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";

/**
 * useChatSocket — opens a persistent WebSocket to /api/ws/chat.
 * onMessage receives every {type, data} payload from the server.
 */
export function useChatSocket(onMessage) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const handlerRef = useRef(onMessage);
  const [connected, setConnected] = useState(false);

  useEffect(() => { handlerRef.current = onMessage; }, [onMessage]);

  const connect = useCallback(() => {
    if (!user) return;
    // Use same-origin WebSocket so the auth cookie is sent first-party (must match /api/* ingress host).
    const origin =
      (typeof window !== "undefined" && window.location && window.location.origin) ||
      process.env.REACT_APP_BACKEND_URL;
    const wsUrl = origin.replace(/^http/, "ws") + "/api/ws/chat";
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch {
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      // reconnect with backoff
      reconnectRef.current = setTimeout(connect, 3000);
    };
    ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    ws.onmessage = (e) => {
      try {
        const payload = JSON.parse(e.data);
        handlerRef.current?.(payload);
      } catch { /* ignore */ }
    };
  }, [user]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      const ws = wsRef.current;
      if (ws) {
        // Clear handlers so reconnect logic doesn't fire on cleanup
        ws.onclose = null;
        ws.onerror = null;
        try {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        } catch { /* noop */ }
      }
    };
  }, [connect]);

  return { connected };
}
